import { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../lib/api'
import CreatePost from './createPost'

const STORY_DURATION_MS = 15000

function formatTime(value) {
	if (!value) return ''
	const date = new Date(value)
	return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function Home({ currentUser, onLogin, city, onOpenProfile, composerRequest, onComposerHandled }) {
	const [stories, setStories] = useState([])
	const [posts, setPosts] = useState([])
	const [loading, setLoading] = useState(false)
	const [loadingMore, setLoadingMore] = useState(false)
	const [page, setPage] = useState(1)
	const [composerOpen, setComposerOpen] = useState(false)
	const [composerKind, setComposerKind] = useState('post')
	const [storyViewerOpen, setStoryViewerOpen] = useState(false)
	const [activeStoryUserIndex, setActiveStoryUserIndex] = useState(0)
	const [activeStoryItemIndex, setActiveStoryItemIndex] = useState(0)
	const [storyProgress, setStoryProgress] = useState(0)
	const [storyViewsModalOpen, setStoryViewsModalOpen] = useState(false)
	const [likesModalPost, setLikesModalPost] = useState(null)
	const [commentsModalPost, setCommentsModalPost] = useState(null)
	const [likesModalLoading, setLikesModalLoading] = useState(false)
	const [commentsModalLoading, setCommentsModalLoading] = useState(false)
	const [modalLikes, setModalLikes] = useState([])
	const [modalComments, setModalComments] = useState([])
	const [modalCommentDraft, setModalCommentDraft] = useState('')
	const [modalCommentSubmitting, setModalCommentSubmitting] = useState(false)

	const canPost = !!currentUser

	async function loadHome() {
		setLoading(true)
		try {
			const res = await apiFetch('/home')
			if (!res.ok) throw new Error('Failed to load home feed')
			const data = await res.json()
			setStories(data.stories || [])
			setPosts((Array.isArray(data.posts) ? data.posts : []).filter((post) => post?.feedType === 'POST'))
			setPage(1)
		} catch {
			setStories([])
			setPosts([])
		} finally {
			setLoading(false)
		}
	}

	async function loadMorePosts() {
		if (loadingMore) return
		setLoadingMore(true)
		try {
			const res = await apiFetch(`/posts?page=${page}&limit=10`)
			if (!res.ok) throw new Error('Failed to load posts')
			const data = await res.json()
			setPosts((prev) => [...prev, ...(Array.isArray(data.posts) ? data.posts : []).filter((post) => post?.feedType === 'POST')])
			setPage((p) => p + 1)
		} catch {
			// non-blocking load more failure
		} finally {
			setLoadingMore(false)
		}
	}

	useEffect(() => {
		loadHome()
	}, [])

	useEffect(() => {
		if (!composerRequest?.id) return
		setComposerKind(composerRequest.kind || 'post')
		setComposerOpen(true)
		onComposerHandled?.()
	}, [composerRequest?.id])

	async function toggleLike(postId) {
		if (!currentUser) return onLogin?.()
		const existingPost = posts.find((post) => post.id === postId)
		if (!existingPost) return

		const previousLiked = !!existingPost.likedByMe
		const previousCount = Number(existingPost.likesCount || 0)
		const optimisticLiked = !previousLiked
		const optimisticCount = Math.max(0, previousCount + (optimisticLiked ? 1 : -1))

		setPosts((prev) => prev.map((post) => (
			post.id === postId
				? { ...post, likedByMe: optimisticLiked, likesCount: optimisticCount }
				: post
		)))

		setLikesModalPost((prev) => (
			prev && prev.id === postId ? { ...prev, likedByMe: optimisticLiked, likesCount: optimisticCount } : prev
		))

		if (likesModalPost?.id === postId && currentUser?.id) {
			setModalLikes((prev) => {
				if (optimisticLiked) {
					const alreadyPresent = prev.some((entry) => entry.user?.id === currentUser.id)
					if (alreadyPresent) return prev
					return [{
						id: `local-${Date.now()}`,
						createdAt: new Date().toISOString(),
						user: {
							id: currentUser.id,
							name: currentUser.name || 'You',
							profilePicture: currentUser.profilePicture || null,
						},
					}, ...prev]
				}

				return prev.filter((entry) => entry.user?.id !== currentUser.id)
			})
		}

		try {
			const res = await apiFetch(`/posts/${postId}/like`, { method: 'POST' })
			if (!res.ok) throw new Error('Failed to toggle like')

			const data = await res.json()
			setPosts((prev) => prev.map((post) => (
				post.id === postId
					? { ...post, likedByMe: data.liked, likesCount: data.likesCount }
					: post
			)))
			setLikesModalPost((prev) => (
				prev && prev.id === postId ? { ...prev, likedByMe: data.liked, likesCount: data.likesCount } : prev
			))
		} catch {
			setPosts((prev) => prev.map((post) => (
				post.id === postId
					? { ...post, likedByMe: previousLiked, likesCount: previousCount }
					: post
			)))
			setLikesModalPost((prev) => (
				prev && prev.id === postId ? { ...prev, likedByMe: previousLiked, likesCount: previousCount } : prev
			))

			if (likesModalPost?.id === postId && currentUser?.id) {
				setModalLikes((prev) => {
					if (previousLiked) {
						const alreadyPresent = prev.some((entry) => entry.user?.id === currentUser.id)
						if (alreadyPresent) return prev
						return [{
							id: `local-${Date.now()}`,
							createdAt: new Date().toISOString(),
							user: {
								id: currentUser.id,
								name: currentUser.name || 'You',
								profilePicture: currentUser.profilePicture || null,
							},
						}, ...prev]
					}

					return prev.filter((entry) => entry.user?.id !== currentUser.id)
				})
			}
		}
	}


	function handlePostDoubleClick(post, event) {
		const target = event.target
		if (target instanceof Element && target.closest('button, input, textarea, a, label')) return
		if (post.likedByMe) return
		toggleLike(post.id)
	}

	async function openLikesModal(post) {
		setLikesModalPost(post)
		setLikesModalLoading(true)
		setModalLikes([])
		try {
			const res = await apiFetch(`/posts/${post.id}/likes`)
			if (!res.ok) throw new Error('Failed to load likes')
			const data = await res.json()
			setModalLikes(Array.isArray(data.likes) ? data.likes : [])
		} catch {
			setModalLikes([])
		} finally {
			setLikesModalLoading(false)
		}
	}

	async function openCommentsModal(post) {
		setCommentsModalPost(post)
		setCommentsModalLoading(true)
		setModalComments([])
		setModalCommentDraft('')
		try {
			const res = await apiFetch(`/posts/${post.id}/comments`)
			if (!res.ok) throw new Error('Failed to load comments')
			const data = await res.json()
			setModalComments(Array.isArray(data.comments) ? data.comments : [])
		} catch {
			setModalComments([])
		} finally {
			setCommentsModalLoading(false)
		}
	}

	async function submitModalComment() {
		if (!currentUser) return onLogin?.()
		if (!commentsModalPost) return

		const text = modalCommentDraft.trim()
		if (!text) return

		setModalCommentSubmitting(true)
		try {
			const res = await apiFetch(`/posts/${commentsModalPost.id}/comments`, {
				method: 'POST',
				body: JSON.stringify({ text }),
			})
			if (!res.ok) return

			const comment = await res.json()
			setModalComments((prev) => [comment, ...prev])
			setPosts((prev) => prev.map((post) => (
				post.id === commentsModalPost.id
					? {
						...post,
						commentsCount: comment.commentsCount,
						comments: [comment, ...(post.comments || [])],
					}
					: post
			)))
			setCommentsModalPost((prev) => (
				prev ? { ...prev, commentsCount: comment.commentsCount } : prev
			))
			setModalCommentDraft('')
		} finally {
			setModalCommentSubmitting(false)
		}
	}

	async function toggleFollowUser(targetUserId, currentlyFollowing) {
		if (!currentUser) return onLogin?.()
		if (!targetUserId || targetUserId === currentUser.id) return

		setPosts((prev) => prev.map((post) => (
			post.user?.id === targetUserId
				? { ...post, isFollowingAuthor: !currentlyFollowing }
				: post
		)))

		try {
			const res = await apiFetch(`/profile/${targetUserId}/follow`, {
				method: currentlyFollowing ? 'DELETE' : 'POST',
			})
			if (!res.ok) throw new Error('Failed to update follow state')
			const data = await res.json()
			setPosts((prev) => prev.map((post) => (
				post.user?.id === targetUserId
					? { ...post, isFollowingAuthor: !!data.following }
					: post
			)))
		} catch {
			setPosts((prev) => prev.map((post) => (
				post.user?.id === targetUserId
					? { ...post, isFollowingAuthor: currentlyFollowing }
					: post
			)))
		}
	}

	const storyUsers = useMemo(() => {
		const groupedByUser = new Map()

		for (const story of stories) {
			const userId = story.user?.id
			if (!userId) continue

			if (!groupedByUser.has(userId)) {
				groupedByUser.set(userId, {
					id: userId,
					user: story.user,
					stories: [],
				})
			}

			groupedByUser.get(userId).stories.push(story)
		}

		return Array.from(groupedByUser.values())
			.map((entry) => ({
				...entry,
				stories: entry.stories
					.slice()
					.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
			}))
			.sort((a, b) => {
				const aLatest = a.stories[a.stories.length - 1]
				const bLatest = b.stories[b.stories.length - 1]
				return new Date(bLatest?.createdAt || 0).getTime() - new Date(aLatest?.createdAt || 0).getTime()
			})
	}, [stories])

	const storyItems = useMemo(() => storyUsers.slice(0, 20), [storyUsers])
	const activeStoryUser = storyItems[activeStoryUserIndex] || null
	const activeStory = activeStoryUser?.stories[activeStoryItemIndex] || null

	function closeStoryViewer() {
		setStoryViewerOpen(false)
		setStoryProgress(0)
		setStoryViewsModalOpen(false)
	}

	function openStoryViewer(userIndex) {
		setActiveStoryUserIndex(userIndex)
		setActiveStoryItemIndex(0)
		setStoryProgress(0)
		setStoryViewerOpen(true)
	}

	function goToNextStory() {
		if (!storyItems.length) return
		const currentUserStories = storyItems[activeStoryUserIndex]?.stories || []

		if (activeStoryItemIndex < currentUserStories.length - 1) {
			setActiveStoryItemIndex((prev) => prev + 1)
			setStoryProgress(0)
			return
		}

		if (activeStoryUserIndex < storyItems.length - 1) {
			setActiveStoryUserIndex((prev) => prev + 1)
			setActiveStoryItemIndex(0)
			setStoryProgress(0)
			return
		}

		setActiveStoryUserIndex(0)
		setActiveStoryItemIndex(0)
		closeStoryViewer()
	}

	function goToPreviousStory() {
		if (!storyItems.length) return

		if (activeStoryItemIndex > 0) {
			setActiveStoryItemIndex((prev) => prev - 1)
			setStoryProgress(0)
			return
		}

		if (activeStoryUserIndex > 0) {
			const previousUserIndex = activeStoryUserIndex - 1
			const previousUserStories = storyItems[previousUserIndex]?.stories || []
			setActiveStoryUserIndex(previousUserIndex)
			setActiveStoryItemIndex(Math.max(previousUserStories.length - 1, 0))
			setStoryProgress(0)
		}
	}

	useEffect(() => {
		if (!storyViewerOpen || !activeStory) return undefined

		setStoryProgress(0)
		const startedAt = Date.now()
		const timer = setInterval(() => {
			const progress = (Date.now() - startedAt) / STORY_DURATION_MS
			if (progress >= 1) {
				clearInterval(timer)
				setStoryProgress(1)
				goToNextStory()
				return
			}

			setStoryProgress(progress)
		}, 100)

		return () => clearInterval(timer)
	}, [storyViewerOpen, activeStory?.id])

	useEffect(() => {
		if (!storyViewerOpen || !activeStory?.id || !currentUser?.id) return

		apiFetch(`/home/stories/${activeStory.id}/view`, { method: 'POST' }).catch(() => {})
	}, [storyViewerOpen, activeStory?.id, currentUser?.id])

	return (
		<section className="mx-auto w-full max-w-3xl">
			<div className="rounded-3xl border border-white/60 bg-white/80 p-4 shadow-lg backdrop-blur">
				<div className="mb-3 flex items-center justify-between sm:hidden">
					
					{canPost && (
						<button onClick={() => { setComposerKind('post'); setComposerOpen(true) }} className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-3 py-1.5 text-xs font-semibold text-white">
							+ Post
						</button>
					)}
				</div>

				<div className="flex items-center justify-between gap-2">
					
					<div className="flex items-center gap-2">
						{canPost ? (
							<button onClick={() => { setComposerKind('post'); setComposerOpen(true) }} className="hidden rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-4 py-2 text-xs font-semibold text-white sm:inline-block">
								+ Add Post
							</button>
						) : (
							<button onClick={onLogin} className="rounded-full border border-orange-300 px-4 py-2 text-xs font-semibold text-orange-700">
								Login to Post
							</button>
						)}
	
					</div>
				</div>

				<div className="mt-4 flex gap-3 overflow-x-auto pb-1">
					{canPost && (
						<button onClick={() => { setComposerKind('story'); setComposerOpen(true) }} className="min-w-[72px] text-center">
							<div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-amber-300 to-orange-500 p-[2px]">
								<div className="flex h-full w-full items-center justify-center rounded-full bg-white text-xl font-bold text-orange-600">+</div>
							</div>
							<p className="mt-1 truncate text-[11px] font-semibold text-gray-700">Your story</p>
						</button>
					)}
					{storyItems.length === 0 && <p className="text-sm text-gray-500">No active stories yet.</p>}
					{storyItems.map((storyUser, index) => (
						<div key={storyUser.id} className="min-w-[72px] text-center">
							<button type="button" onClick={() => openStoryViewer(index)} className="block w-full">
								<div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-orange-300 to-red-400 p-[2px]">
									<img
										src={storyUser.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(storyUser.user.name)}`}
										alt={storyUser.user.name}
										className="h-full w-full rounded-full bg-white object-cover"
									/>
								</div>
							</button>
							<button
								type="button"
								onClick={() => onOpenProfile?.(storyUser.user?.id)}
								disabled={!storyUser.user?.id}
								className="mt-1 w-full truncate text-[11px] font-semibold text-gray-700 hover:text-orange-700 disabled:opacity-70"
							>
								{storyUser.user.name}
							</button>
						</div>
					))}
				</div>
			</div>

			<div className="mt-4 space-y-4">
				{loading && <p className="text-sm font-semibold text-gray-600">Loading feed...</p>}

				{!loading && posts.length === 0 && (
					<div className="rounded-3xl border border-gray-200 bg-white p-5 text-sm text-gray-600">
						No posts yet. Be the first one to share your dining experience.
					</div>
				)}

				{posts.map((post) => (
					<article
						key={post.id}
						onDoubleClick={(event) => handlePostDoubleClick(post, event)}
						className="rounded-3xl border border-white/60 bg-white/90 p-4 shadow-lg"
					>
						<div className="flex items-start gap-3">
							<button onClick={() => onOpenProfile?.(post.user?.id)} className="shrink-0">
								<img
									src={post.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(post.user.name)}`}
									alt={post.user.name}
									className="h-10 w-10 rounded-xl object-cover"
								/>
							</button>
							<div className="min-w-0 flex-1">
								<div className="flex items-start justify-between gap-2">
									<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
										<button onClick={() => onOpenProfile?.(post.user?.id)} className="truncate font-semibold text-gray-900 hover:text-orange-700">
											{post.user.name}
										</button>
										{Array.isArray(post.taggedUsers) && post.taggedUsers.length > 0 && (
											<>
												<span className="text-xs font-semibold text-gray-500">and</span>
												{post.taggedUsers.map((user) => (
													<button
														key={user.id || user.name}
														type="button"
														onClick={() => onOpenProfile?.(user.id)}
														disabled={!user.id}
														className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800 disabled:opacity-70"
													>
														<img
															src={user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(user.name)}`}
															alt={user.name}
															className="h-4 w-4 rounded-full object-cover"
														/>
														{user.name}
													</button>
												))}
											</>
										)}
										<p className="text-xs text-gray-500">{formatTime(post.createdAt)}</p>
										<span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700">
											{post.postType?.replaceAll('_', ' ')}
										</span>
									</div>
									{currentUser && post.user?.id && post.user.id !== currentUser.id && (
										<button
											onClick={() => toggleFollowUser(post.user.id, !!post.isFollowingAuthor)}
											className={`rounded-full px-3 py-1 text-xs font-semibold ${post.isFollowingAuthor ? 'border border-gray-300 text-gray-700' : 'bg-orange-500 text-white'}`}
										>
											{post.isFollowingAuthor ? 'Following' : 'Follow'}
										</button>
									)}
								</div>
								{post.restaurant && (
									<p className="mt-0.5 text-xs font-semibold text-amber-700">
										{post.restaurant.name} • {post.restaurant.area}
									</p>
								)}
								{post.content && <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap">{post.content}</p>}
								{post.mediaUrl && post.mediaType === 'VIDEO' && (
									<video src={post.mediaUrl} controls playsInline className="mt-3 w-full rounded-2xl border border-gray-200 bg-black object-contain max-h-[32rem]" />
								)}
								{post.mediaUrl && post.mediaType !== 'VIDEO' && (
									<img src={post.mediaUrl} alt="post" className="mt-3 w-full rounded-2xl border border-gray-200 object-cover max-h-120" />
								)}
								<div className="mt-3 flex flex-wrap gap-2 text-[11px] text-gray-600">
									{post.cuisineTag && <span className="rounded-full bg-blue-100 px-2 py-1 font-semibold text-blue-700">{post.cuisineTag}</span>}
									{post.isLookingForCoeater && <span className="rounded-full bg-green-100 px-2 py-1 font-semibold text-green-700">Looking for co-eaters</span>}
									{/* {post.seats && <span className="rounded-full bg-gray-100 px-2 py-1 font-semibold text-gray-700">{post.seats} seats</span>} */}
								</div>
							</div>
						</div>

						<div className="mt-3 flex items-center gap-4 text-sm">
							<button
								onClick={() => toggleLike(post.id)}
								aria-label="Like post"
								className={`rounded-full border p-2 font-semibold ${post.likedByMe ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-300 text-gray-700'}`}
							>
								{post.likedByMe ? '♥' : '♡'}
							</button>
							<button onClick={() => openLikesModal(post)} className="font-semibold text-gray-700 hover:text-gray-900">
								{post.likesCount || 0}
							</button>

							<button
								onClick={() => openCommentsModal(post)}
								aria-label="Open comments"
								className="rounded-full border border-gray-300 p-2 text-gray-700"
							>
								💬
							</button>
							<button onClick={() => openCommentsModal(post)} className="font-semibold text-gray-700 hover:text-gray-900">
								{post.commentsCount || 0}
							</button>
						</div>
					</article>
				))}

				{posts.length > 0 && (
					<div className="pb-6 text-center">
						<button onClick={loadMorePosts} disabled={loadingMore} className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-60">
							{loadingMore ? 'Loading...' : 'Load More Posts'}
						</button>
					</div>
				)}
			</div>

			<CreatePost
				open={composerOpen}
				onClose={() => setComposerOpen(false)}
				onCreated={(item, kind) => {
					if (kind === 'story') {
						setStories((prev) => [item, ...prev])
						return
					}
					if (kind === 'post') {
						setPosts((prev) => [item, ...prev])
					}
				}}
				city={city}
				initialKind={composerKind}
			/>

			{storyViewerOpen && activeStory && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-3">
					<div className="w-full max-w-md overflow-hidden rounded-3xl border border-white/20 bg-gray-950 text-white shadow-2xl">
						<div className="flex gap-1 px-3 pt-3">
							{activeStoryUser?.stories.map((story, index) => (
								<div key={story.id} className="h-1 flex-1 overflow-hidden rounded bg-white/25">
									<div
										className="h-full bg-white"
										style={{
											width: `${index < activeStoryItemIndex ? 100 : index === activeStoryItemIndex ? Math.max(0, Math.min(100, storyProgress * 100)) : 0}%`,
											transition: 'width 100ms linear',
										}}
									/>
								</div>
							))}
						</div>
						<div className="flex items-center gap-3 border-b border-white/15 p-3">
							<button type="button" onClick={() => onOpenProfile?.(activeStory.user?.id)} disabled={!activeStory.user?.id} className="shrink-0">
								<img
									src={activeStory.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(activeStory.user.name)}`}
									alt={activeStory.user.name}
									className="h-9 w-9 rounded-full object-cover"
								/>
							</button>
							<div className="min-w-0 flex-1">
								<button type="button" onClick={() => onOpenProfile?.(activeStory.user?.id)} disabled={!activeStory.user?.id} className="truncate text-left text-sm font-semibold hover:text-orange-200 disabled:opacity-70">
									{activeStory.user.name}
								</button>
								<p className="text-xs text-gray-300">{formatTime(activeStory.createdAt)}</p>
							</div>
							<button type="button" onClick={closeStoryViewer} className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">Close</button>
						</div>

						<div className="relative">
							{activeStory.mediaUrl ? (
								activeStory.mediaType === 'VIDEO'
									? <video src={activeStory.mediaUrl} autoPlay controls playsInline className="h-[60vh] w-full bg-black object-contain" />
									: <img src={activeStory.mediaUrl} alt="Story" className="h-[60vh] w-full object-cover" />
							) : (
								<div className="flex h-[60vh] w-full items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 p-6 text-center text-lg font-semibold">
									{activeStory.caption || 'Story'}
								</div>
							)}
							<button
								type="button"
								onClick={goToPreviousStory}
								className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/45 px-2 py-1 text-lg"
							>
								‹
							</button>
							<button
								type="button"
								onClick={goToNextStory}
								className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/45 px-2 py-1 text-lg"
							>
								›
							</button>
						</div>

						{activeStory.caption && (
							<div className="border-t border-white/15 p-3 text-sm text-gray-100">
								{activeStory.caption}
							</div>
						)}
						{currentUser?.id === activeStory.user?.id && typeof activeStory.viewersCount === 'number' && (
							<div className="flex justify-end border-t border-white/15 px-4 py-3">
								<button
									type="button"
									onClick={() => setStoryViewsModalOpen(true)}
									className="rounded-full bg-white/10 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-white/15"
								>
									Seen by {activeStory.viewersCount}
								</button>
							</div>
						)}
					</div>
				</div>
			)}

			{storyViewsModalOpen && activeStory && (
				<div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-3">
					<div className="w-full max-w-md overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
							<h3 className="text-base font-bold text-gray-900">Seen by</h3>
							<button type="button" onClick={() => setStoryViewsModalOpen(false)} className="text-sm font-semibold text-gray-500">Close</button>
						</div>
						<div className="max-h-[60vh] overflow-y-auto p-3">
							{!activeStory.viewers?.length && <p className="text-sm text-gray-500">No views yet.</p>}
							{activeStory.viewers?.map((entry) => (
								<div key={entry.id} className="flex items-center gap-3 rounded-xl px-2 py-2">
									<button type="button" onClick={() => onOpenProfile?.(entry.user?.id)} className="shrink-0">
										<img
											src={entry.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(entry.user.name)}`}
											alt={entry.user.name}
											className="h-10 w-10 rounded-full object-cover"
										/>
									</button>
									<div className="min-w-0 flex-1">
										<button type="button" onClick={() => onOpenProfile?.(entry.user?.id)} className="truncate text-sm font-semibold text-gray-900">{entry.user.name}</button>
										<p className="text-xs text-gray-500">{formatTime(entry.createdAt)}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{likesModalPost && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-3">
					<div className="w-full max-w-md overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
							<h3 className="text-base font-bold text-gray-900">Liked by</h3>
							<button type="button" onClick={() => setLikesModalPost(null)} className="text-sm font-semibold text-gray-500">Close</button>
						</div>
						<div className="max-h-[60vh] overflow-y-auto p-3">
							{likesModalLoading && <p className="text-sm text-gray-500">Loading likes...</p>}
							{!likesModalLoading && modalLikes.length === 0 && <p className="text-sm text-gray-500">No likes yet.</p>}
							{modalLikes.map((entry) => (
								<div key={entry.id} className="flex items-center gap-3 rounded-xl px-2 py-2">
									<button type="button" onClick={() => onOpenProfile?.(entry.user?.id)} disabled={!entry.user?.id} className="shrink-0">
										<img
											src={entry.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(entry.user.name)}`}
											alt={entry.user.name}
											className="h-10 w-10 rounded-full object-cover"
										/>
									</button>
									<div className="min-w-0 flex-1">
										<button type="button" onClick={() => onOpenProfile?.(entry.user?.id)} disabled={!entry.user?.id} className="truncate text-left text-sm font-semibold text-gray-900 hover:text-orange-700 disabled:opacity-70">{entry.user.name}</button>
										<p className="text-xs text-gray-500">{formatTime(entry.createdAt)}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{commentsModalPost && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-3">
					<div className="flex h-[75vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
							<h3 className="text-base font-bold text-gray-900">Comments</h3>
							<button type="button" onClick={() => setCommentsModalPost(null)} className="text-sm font-semibold text-gray-500">Close</button>
						</div>
						<div className="flex-1 overflow-y-auto p-3">
							{commentsModalLoading && <p className="text-sm text-gray-500">Loading comments...</p>}
							{!commentsModalLoading && modalComments.length === 0 && <p className="text-sm text-gray-500">No comments yet.</p>}
							{modalComments.map((comment) => (
								<div key={comment.id} className="mb-2 rounded-xl bg-gray-50 px-3 py-2">
									<button type="button" onClick={() => onOpenProfile?.(comment.user?.id)} disabled={!comment.user?.id} className="text-left text-xs font-semibold text-gray-800 hover:text-orange-700 disabled:opacity-70">{comment.user.name}</button>
									<p className="text-sm text-gray-700">{comment.text}</p>
									<p className="mt-1 text-[11px] text-gray-500">{formatTime(comment.createdAt)}</p>
								</div>
							))}
						</div>
						<div className="border-t border-gray-200 p-3">
							<div className="flex gap-2">
								<input
									value={modalCommentDraft}
									onChange={(e) => setModalCommentDraft(e.target.value)}
									className="flex-1 rounded-full border border-gray-300 px-4 py-2 text-sm"
									placeholder="Write a comment..."
								/>
								<button
									onClick={submitModalComment}
									disabled={modalCommentSubmitting}
									className="rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
								>
									{modalCommentSubmitting ? 'Sending...' : 'Comment'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</section>
	)
}
